// The Ask AI bubble (Studio, Phelipe 2026-10-16): one chat icon in the
// bottom-right corner with three doors — Support (questions; a person when
// needed), Studio (changes to the company's own forms and lists, previewed
// first) and the Feedback Loop (the existing Instant Feedback panel).
//
// Every message goes to POST /studio/chat; the server routes it and answers
// with an intent: a reply, a Studio preview card (HTML), a written-up
// Feedback Loop draft, help-article links, or a hand-off to a person. The
// element is turbo-permanent, so the conversation survives moving between
// pages; the page context is read from the feedback-context meta at send
// time, never cached.
import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { showToast } from "../lib/toast"
import { pickSpot, hasSpots } from "../lib/studio_spot_picker"
import { placeBlock } from "../lib/page_blocks"
import { placeField } from "../lib/custom_fields"

const MAX_HISTORY = 16
const STORE_KEY = "studio-chat:v1"
// Tailwind's purple-700, the color every Studio tag and button already wears.
const STUDIO_PURPLE = "#7e22ce"
// Docked beside the page the window is dark, and the purple goes darker with it (Phelipe 2026-10-17).
const STUDIO_PURPLE_DARK = "#5b21b6"
// The bar at the top of an open card wears the card's color (Phelipe
// 2026-10-17): Studio purple, Get help blue-600, Feedback Loop green-700
// (dark enough for white words).
const SUPPORT_BLUE = "#2563eb"
const FEEDBACK_GREEN = "#16a34a"
// Studio docked beside the page (Phelipe 2026-10-17): the window's width at
// the right edge, and the narrowest screen it does that on.
const DOCK_WIDTH = 380
const DOCK_MIN_VIEWPORT = 1100
// Speak instead of typing (Phelipe 2026-10-17): the longest one recording may run.
const DICTATION_MAX_SECONDS = 180

export default class extends Controller {
  static targets = ["panel", "launcher", "faceIcon", "closeIcon", "messages", "home", "input", "sendButton", "subtitle", "back", "clearConfirm",
                    "composer", "article", "articleBody", "articleTitle", "articleLink",
                    "tabHome", "tabMessages", "msgs", "msgList", "msgEmpty", "thread", "threadTitle", "threadStatus", "threadBody", "threadReply", "threadInput", "threadSend", "threadLink",
                    "help", "helpSearch", "helpBack", "helpBackTitle", "helpList", "tabHelp", "articleSize", "header", "tabs",
                    "homeHead", "flowBar", "flowTitle", "clearBtn", "pageLine", "dockBtn", "micBtn", "micTimer"]
  static values = {
    chatUrl: String, changeUrl: String, feedbackUrl: String, feedbackPageUrl: String, messagesUrl: String,
    intercom: Boolean, supportEmail: String, user: Number, i18n: Object, helpUrl: String, conversationsUrl: String
  }

  // The remembered conversation is this person's: a colleague signing in on
  // the same browser never sees it.
  get storeKey() { return `${STORE_KEY}:${this.userValue || 0}` }

  connect() {
    if (this.element.dataset.wired) return // turbo-permanent: connect can fire again
    this.element.dataset.wired = "1"
    this.history = []
    this._door = null
    this._conv = null // the open server-side conversation {id, kind, done}
    this.pending = false
    this._restore()
    // Home is the three cards only (Phelipe 2026-10-17: "click Support, Studio
    // or Feedback Loop to continue"): the typing box shows inside a conversation.
    if (!this.homeTarget.classList.contains("hidden")) this.composerTarget.hidden = true
    this._onOpen = (e) => { this.open(); if (e.detail?.door) this.pickDoor(e.detail.door); if (e.detail?.block) this._pickBlock(e.detail.block) }
    window.addEventListener("studio-chat:open", this._onOpen)
    // The wide finance modals hide Intercom's launcher; ours steps aside too.
    this._onHide = () => this.launcherTarget.classList.add("hidden")
    this._onShow = () => this.launcherTarget.classList.remove("hidden")
    window.addEventListener("studio:launcher-hide", this._onHide)
    window.addEventListener("studio:launcher-show", this._onShow)
    // While the Feedback Loop form is inside, Escape is its (it closes the form, we get our screens back).
    // Capture phase: this runs before the form's own Escape handler, which
    // hands our screens back in the same keystroke.
    this._onKey = (e) => { if (e.key === "Escape" && this.isOpen && !this._fbHosted && !this._dtHosted) this.close() }
    document.addEventListener("keydown", this._onKey, true)
    // The Feedback Loop form lives inside this window when opened from here
    // (Phelipe 2026-10-17); it tells us when it shows, steps aside to draw or
    // record, and leaves. See instant_feedback_controller.js#_enterHost.
    this._onFbHost = (e) => {
      if (e.detail?.host !== "studio") return
      const phase = e.detail.phase
      if (phase === "hidden") { this._fbAside = true; this.close() }
      else if (phase === "shown") { this._fbAside = false; this._fbHosted = true; this._fbShell(true); if (!this.isOpen) this._show() }
      else if (phase === "left") { this._fbAside = false; this._fbCard = null; if (this._fbHosted) { this._fbHosted = false; this._fbRestore() } }
    }
    window.addEventListener("instant-feedback:host", this._onFbHost)
    this._onDockResize = () => this._applyDock()
    window.addEventListener("resize", this._onDockResize)
    // The hosted Feedback Loop form sent a request or kept a draft: say so in the
    // conversation and stamp the card it came from (Phelipe 2026-10-17).
    this._onFbSent = (e) => this._fbSent(e.detail || {})
    window.addEventListener("instant-feedback:sent", this._onFbSent)
    // A request's details panel (feedback-detail) inside this window, opened from
    // Messages (Phelipe 2026-10-17: "show the window as if they clicked it on the
    // Feedback Loop page — but here"): the same phases as the form.
    this._onDtHost = (e) => {
      if (e.detail?.host !== "studio") return
      if (e.detail.phase === "shown") { this._dtHosted = true; this._fbShell(true); this._setHeader("detail"); if (!this.isOpen) this._show() }
      else if (e.detail.phase === "left") { if (this._dtHosted) { this._dtHosted = false; this._dtRestore() } }
    }
    window.addEventListener("feedback-detail:host", this._onDtHost)
    // "Open in the Feedback Loop" from that panel: the window steps out of the way.
    this._onDtNavigate = () => { if (this.isOpen) this.close() }
    window.addEventListener("feedback-detail:navigate", this._onDtNavigate)
    // "Draw where" (Phelipe 2026-10-17): the pen runs over the page — the window
    // steps aside, and the picture of the spot comes back into the chat as the
    // next message (instant_feedback_controller.js dispatches these).
    this._onDrawing = (e) => { if (e.detail?.host !== "studio") return; this._drawAside = true; this.close() }
    this._onDrawn = (e) => { if (e.detail?.host !== "studio") return; this._drawAside = false; this._show(); this._sendImage(e.detail.dataUrl) }
    this._onDrawCancelled = (e) => { if (e.detail?.host !== "studio") return; this._drawAside = false; this._show(); this._appendAi(this.t.draw_cancelled, "studio") }
    window.addEventListener("instant-feedback:drawing", this._onDrawing)
    window.addEventListener("instant-feedback:drawn", this._onDrawn)
    window.addEventListener("instant-feedback:draw-cancelled", this._onDrawCancelled)
    // The page line under the Studio bar follows the person from page to page.
    this._onPage = () => this._paintPageLine()
    document.addEventListener("turbo:load", this._onPage)
    this._onClick = (e) => this._onMessagesClick(e)
    this.messagesTarget.addEventListener("click", this._onClick)
    this._onArticleClick = (e) => this._onArticleBodyClick(e)
    this.articleBodyTarget.addEventListener("click", this._onArticleClick)
    this._onMsgClick = (e) => {
      const row = e.target.closest("[data-studio-thread], [data-studio-change], [data-studio-conversation]")
      if (!row) return
      if (row.dataset.studioConversation) this.openConversation(+row.dataset.studioConversation)
      else if (row.dataset.studioChange) this.openChange(+row.dataset.studioChange, row.dataset.title)
      else this.openThread(+row.dataset.studioThread, row.dataset.title, row.dataset.draft === "1")
    }
    this.msgListTarget.addEventListener("click", this._onMsgClick)
    // A Studio card opened from Messages keeps its buttons (Undo).
    this.threadBodyTarget.addEventListener("click", this._onClick)
  }

  get t() { return this.i18nValue || {} }
  get isOpen() { return !this.panelTarget.classList.contains("hidden") }

  // ── Open / close ─────────────────────────────────────────────────

  toggle() { this.isOpen ? this.close() : this.open() }

  open() {
    if (this.isOpen) return
    if (this._fbAside || this._drawAside || this._pickAside) return // the Feedback Loop form, the pen or the dot stepped aside; they bring us back
    this._show()
    track("Ask AI Opened", { source: "bubble", has_project: !!this._context().project_id })
    // Home starts at the top (the hello and the cards); a conversation at its latest line.
    const onHome = !this.homeTarget.classList.contains("hidden")
    this._scroll(onHome)
    if (!onHome) this.inputTarget.focus()
  }

  _show() {
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.classList.add("flex")
    this.faceIconTarget.classList.add("hidden")
    this.closeIconTarget.classList.remove("hidden")
    this.launcherTarget.setAttribute("aria-label", this.t.close || "Close")
    this._applyDock()
  }

  // ── Studio beside the page (Phelipe 2026-10-17) ────────────────────
  // In the Studio flow on a wide screen the window docks to the right edge,
  // full height, and the page slides left to make room — the whole page
  // stays in view while Studio works on it (like Claude Code beside an
  // editor). Support and the Feedback Loop keep the floating window. The
  // bar's dock button flips it, and the choice is remembered on this browser.

  get _dockPref() {
    try { return localStorage.getItem("studio.dock") !== "0" } catch (e) { return true }
  }

  _canDock() { return window.innerWidth >= DOCK_MIN_VIEWPORT }

  // Only inside the Studio flow (Home keeps the window even when the last chat was Studio).
  _wantsDock() { return this._door === "studio" && this.homeTarget.classList.contains("hidden") && this._dockPref && this._canDock() }

  _applyDock() {
    const on = this.isOpen && this._wantsDock()
    if (this.hasDockBtnTarget) {
      const show = this._door === "studio" && this._canDock()
      this.dockBtnTarget.hidden = !show
      const label = on ? this.dockBtnTarget.dataset.labelUndock : this.dockBtnTarget.dataset.labelDock
      this.dockBtnTarget.title = label
      this.dockBtnTarget.setAttribute("aria-label", label)
      this.dockBtnTarget.querySelector('[data-dock-icon="dock"]')?.classList.toggle("hidden", on)
      this.dockBtnTarget.querySelector('[data-dock-icon="undock"]')?.classList.toggle("hidden", !on)
    }
    if (on === !!this._docked) return
    this._docked = on
    const panel = this.panelTarget
    if (on) {
      this._floatStyle = panel.getAttribute("style") || ""
      // Dark while docked (Phelipe 2026-10-17: "that special touch, like they are
      // editing the tool"): data-theme="dark" on the panel alone flips the app's
      // color variables for everything inside it; the purples the Studio cards
      // use aren't in that set, so they get dark values here.
      panel.setAttribute("style", `top:0;right:0;bottom:0;width:${DOCK_WIDTH}px;max-width:100vw;height:auto;max-height:none;border-radius:0;border-left:1px solid #3f3f46;z-index:79;` +
        "--color-purple-50:#2a1546;--color-purple-100:#3b1d63;--color-purple-200:#5b2d9a;--color-purple-600:#a855f7;--color-purple-700:#d8b4fe;--color-purple-800:#e9d5ff;--color-purple-900:#f3e8ff")
      panel.setAttribute("data-theme", "dark")
      this._paintAccent()
      // The room for the panel is padding on <html>, not <body>: Turbo swaps
      // the body on every page change and the next page would slide back under
      // the panel (Phelipe 2026-10-17). Set at once, no transition: a transition
      // on that padding never finished in a background tab.
      document.documentElement.style.setProperty("padding-right", `${DOCK_WIDTH}px`, "important")
      document.documentElement.classList.add("studio-docked")
      this.launcherTarget.style.right = `${DOCK_WIDTH + 20}px`
    } else {
      panel.setAttribute("style", this._floatStyle || "")
      panel.removeAttribute("data-theme")
      document.documentElement.style.removeProperty("padding-right")
      this._paintAccent()
      document.documentElement.classList.remove("studio-docked")
      this.launcherTarget.style.right = "20px"
    }
  }

  toggleDock() {
    const next = !this._docked
    try { localStorage.setItem("studio.dock", next ? "1" : "0") } catch (e) { /* private mode */ }
    this._applyDock()
    track("Studio Panel Docked", { docked: next })
  }

  close() {
    if (this._fbHosted && !this._fbAside) window.dispatchEvent(new CustomEvent("instant-feedback:leave"))
    if (this._dtHosted) window.dispatchEvent(new CustomEvent("feedback-detail:leave"))
    this.panelTarget.classList.add("hidden")
    this.panelTarget.classList.remove("flex")
    this.faceIconTarget.classList.remove("hidden")
    this.closeIconTarget.classList.add("hidden")
    this.launcherTarget.setAttribute("aria-label", this.t.open || "ConsLog")
    this.cancelClear()
    this._applyDock()
  }

  // ── The Feedback Loop form inside this window ────────────────────
  // `back` is the screen to come back to when the form is left: the Home
  // cards, the conversation, or the Messages list.
  _hostFeedback(detail, back) {
    this._fbReturn = back
    this._fbHosted = true
    if (this._articleOpen) this.closeArticle()
    this._fbShell(true)
    this._setHeader("feedback")
    const ev = detail.resume
      ? new CustomEvent("instant-feedback:resume", { detail: { id: detail.resume, host: "studio" } })
      : new CustomEvent("instant-feedback:open", { detail: { ...detail, host: "studio" } })
    window.dispatchEvent(ev)
  }

  // Only the header stays; the form covers everything under it. Coming back,
  // only the frame returns (chat area + tabs): the screen we return to shows
  // its own panes — un-hiding everything here stacked the Help pane above
  // Home's cards (Phelipe 2026-10-17).
  _fbShell(on) {
    if (on) {
      ;[this.messagesTarget, this.composerTarget, this.msgsTarget, this.threadTarget, this.helpTarget].forEach((el) => { el.hidden = true })
      if (this.hasTabsTarget) this.tabsTarget.hidden = true
      return
    }
    this.messagesTarget.hidden = false
    if (this.hasTabsTarget) this.tabsTarget.hidden = false
  }

  _fbRestore() {
    this._fbShell(false)
    const back = this._fbReturn || "home"
    this._fbReturn = null
    if (back === "messages") return this.showMessages()
    if (back === "chat" && this.history.length) {
      this._leaveHelp()
      this.msgsTarget.hidden = true
      this.threadTarget.hidden = true
      this.messagesTarget.hidden = false
      this._inChat()
      this._paintTabs("home")
      return this._scroll()
    }
    this.showHome()
  }

  // Back from a request's details panel: Messages again (its list refreshed), or Home.
  _dtRestore() {
    this._fbShell(false)
    const back = this._dtReturn || "messages"
    this._dtReturn = null
    if (back === "messages") return this.showMessages()
    if (back === "chat" && this.history.length) {
      this._leaveHelp()
      this.msgsTarget.hidden = true
      this.threadTarget.hidden = true
      this.messagesTarget.hidden = false
      this._inChat()
      this._paintTabs("home")
      return this._scroll()
    }
    this.showHome()
  }

  // ── Conversations (Phelipe 2026-10-17, like Intercom's) ──────────
  // One thread per flow, kept on the server (StudioConversation): a Home card
  // starts one, its first line opens it, every line goes up as it is said (in
  // order, best effort), Messages lists them all, tapping one brings it back,
  // and it ends by itself when its request is sent or its change approved or
  // undone — then the next line starts a new one. sessionStorage only caches the open one.
  _convKind() { return this._door === "studio" ? "studio" : this._door === "feedback" ? "feedback" : "support" }

  _convLine(role, content, card = null) {
    if (!this.conversationsUrlValue || !content) return
    this._convQueue = (this._convQueue || Promise.resolve()).then(() => this._convSend(role, content, card)).catch(() => {})
  }

  async _convSend(role, content, card) {
    if (!this._conv || this._conv.done || this._conv.kind !== this._convKind()) {
      if (role !== "user") return // an AI line never opens a conversation on its own
      const data = await this._post(this.conversationsUrlValue, { kind: this._convKind(), first_message: content })
      if (data && data.conversation && data.conversation.id) {
        this._conv = { id: data.conversation.id, kind: data.conversation.kind, done: false }
        this._persist()
        track("Ask AI Conversation Started", { kind: this._conv.kind })
      }
      return
    }
    await this._post(`${this.conversationsUrlValue}/${this._conv.id}/messages`, { role, content, card })
  }

  _convClose(outcome) {
    if (!this.conversationsUrlValue || !this._conv || this._conv.done) return
    const conv = this._conv
    conv.done = true
    this._persist()
    this._convQueue = (this._convQueue || Promise.resolve()).then(() => this._patch(`${this.conversationsUrlValue}/${conv.id}/close`, outcome || {})).catch(() => {})
    track("Ask AI Conversation Ended", { kind: conv.kind, outcome: outcome && outcome.outcome_type })
  }

  // A clean thread for a new conversation: the lines go, Home's own blocks stay.
  _startFresh() {
    this.history = []
    this._conv = null
    this._introDoor = null
    Array.from(this.messagesTarget.children).forEach((el) => { if (!el.hasAttribute("data-permanent")) el.remove() })
  }

  // A conversation from Messages comes back as it was: its lines, its mode, its
  // Studio cards (fetched fresh, so Undo still works), and the request it ended with.
  async openConversation(id) {
    const data = await this._getJson(`${this.conversationsUrlValue}/${id}`)
    const c = data && data.conversation
    if (!c) { showToast(this.t.network_error, "error"); return }
    this._startFresh()
    this._conv = { id: c.id, kind: c.kind, done: c.status === "done" }
    this._door = c.kind === "studio" ? "studio" : "support"
    this._introDoor = this._door
    const lines = (c.messages || []).filter((m) => m && m.content)
    this.history = lines.map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content })).slice(-MAX_HISTORY)
    this._paintAccent()
    for (const m of lines) {
      if (m.role === "user") this._appendUser(m.content)
      else this._appendAi(m.content, this._door === "studio" ? "studio" : null)
      if (m.card && m.card.kind === "change" && m.card.id) await this._appendChangeCard(m.card.id)
    }
    if (c.outcome_type === "CustomerRequest" && c.outcome_id) {
      this._appendButtons([{ act: "open-request", id: c.outcome_id, label: (this.t.conv_open_request || "Open #%{id}").replace("%{id}", c.outcome_id) }])
    }
    this._msgsOpen = false
    this._leaveHelp()
    this.msgsTarget.hidden = true
    this.threadTarget.hidden = true
    this.messagesTarget.hidden = false
    this._inChat()
    this._paintTabs("messages")
    this._persist()
    track("Ask AI Conversation Resumed", { kind: c.kind, turns: this.history.length })
    this._scroll()
  }

  async _appendChangeCard(id) {
    const data = await this._getJson(`${this.changeUrlValue}/${id}`)
    if (data && data.card_html) this._appendHtml(data.card_html)
  }

  async _patch(url, body) {
    const res = await fetch(url, { method: "PATCH", credentials: "same-origin", body: JSON.stringify(body || {}),
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } }).catch(() => null)
    return res && res.ok ? res.json().catch(() => null) : null
  }

  // ── Doors ────────────────────────────────────────────────────────

  door(e) { this.pickDoor(e.currentTarget.dataset.door) }

  pickDoor(door) {
    if (door === "feedback") {
      track("Ask AI Message Sent", { door: "feedback", in_chat: true })
      this._hostFeedback({ source: "studio" }, "home")
      return
    }
    if (door === "person") return this.person()
    this._door = door
    this._paintAccent()
    // A Home card starts a new conversation (Phelipe 2026-10-17, like Intercom's
    // "Send us a message"); the one before it waits under Messages. Its intro
    // line opens it.
    this._startFresh()
    this._inChat()
    this._appendAi(door === "studio" ? this.t.studio_intro : this.t.support_intro, door === "studio" ? "studio" : null)
    this._introDoor = door
    this.inputTarget.focus()
    this._persist()
  }

  // Purple while the Studio door is open (Phelipe 2026-10-16: "so the user
  // knows"); the brand color otherwise. Header, send button and the person's
  // own bubbles follow it.
  _paintAccent() {
    if (this._accentDefault === undefined) this._accentDefault = this.element.style.getPropertyValue("--studio-accent")
    this.element.style.setProperty("--studio-accent", this._door === "studio" ? (this._docked ? STUDIO_PURPLE_DARK : STUDIO_PURPLE) : this._accentDefault)
  }

  home() {
    // ‹ while the Feedback Loop form is inside the window: the form's own back on an
    // inner step, or leaving it from its first screen (its "left" brings Home back).
    if (this._fbHosted && !this._fbAside) { window.dispatchEvent(new CustomEvent("instant-feedback:back")); return }
    // ‹ while a request's details panel is inside the window: its inner step first, then back to Messages.
    if (this._dtHosted) { window.dispatchEvent(new CustomEvent("feedback-detail:back")); return }
    if (this._articleOpen) return this.closeArticle()
    if (this._helpOpen) return this.showHome()
    if (this._threadOpen) return this.closeThread()
    if (this._msgsOpen) return this.showHome()
    this._goHome()
  }

  // The Home screen: greeting + cards, no chat lines and no typing box — a
  // conversation starts from a card, and past ones live under Messages.
  _goHome() {
    this._door = null
    this._paintAccent()
    this.homeTarget.classList.remove("hidden")
    this._homeOnly(true)
    this.composerTarget.hidden = true
    this._setHeader(null)
    this._scroll(true)
    this._persist()
  }

  // The top of the window (mock v32): Home shows the ConsLog block with the
  // hello; a flow shows ‹ back, the flow's name and ×. Purple while Studio is
  // open (Phelipe: "so the user knows"), white otherwise. The trash (clear the
  // conversation) only inside a chat.
  _setHeader(flow) {
    const home = !flow
    this.homeHeadTarget.hidden = !home
    this.flowBarTarget.hidden = home
    if (home) { this._paintPageLine(); this._applyDock(); return }
    const titles = { support: this.t.flow_help, studio: this.t.flow_studio, feedback: this.t.flow_feedback, detail: this.t.flow_feedback,
                     messages: this.t.tab_messages, help: this.t.tab_help }
    this.flowTitleTarget.textContent = titles[flow] || this.t.title || "ConsLog"
    const iconFlow = flow === "detail" ? "feedback" : flow
    this.flowBarTarget.querySelectorAll("[data-flow-icon]").forEach((el) => { el.hidden = el.dataset.flowIcon !== iconFlow })
    // The card's color while it's open (Phelipe 2026-10-17): Studio purple,
    // Get help blue, Feedback Loop green; Messages and Help stay white.
    const barColor = { studio: "var(--studio-accent)", support: SUPPORT_BLUE, feedback: FEEDBACK_GREEN, detail: FEEDBACK_GREEN }[flow]
    const colored = !!barColor
    this.flowBarTarget.style.background = barColor || ""
    this.flowBarTarget.classList.toggle("text-white", colored)
    this.flowBarTarget.classList.toggle("text-gray-900", !colored)
    this.flowBarTarget.classList.toggle("border-b", !colored)
    if (this.hasClearBtnTarget) this.clearBtnTarget.hidden = !(flow === "support" || flow === "studio")
    this._paintPageLine()
    this._applyDock()
  }

  // "You're on: Feedback Loop · Canyon Glen" under the Studio bar (Phelipe
  // 2026-10-17): the person sees Studio knows the page, so "add it here" works.
  _paintPageLine() {
    if (!this.hasPageLineTarget) return
    const show = this._door === "studio" && this.hasFlowBarTarget && !this.flowBarTarget.hidden
    this.pageLineTarget.hidden = !show
    if (!show) return
    // The page only — never the project's name (Phelipe 2026-10-17: it reads as
    // if the change were something inside that project).
    const c = this._context()
    this.pageLineTarget.textContent = (this.t.on_page || "You're on: %{page}").replace("%{page}", c.page || "")
  }

  // Home's "Search for help": the Help tab, cursor in the box.
  searchHelp() {
    this.showHelp()
    setTimeout(() => this.helpSearchTarget.focus(), 60)
  }

  // Home shows only the greeting and the cards (Phelipe 2026-10-16: "no need
  // to show messages here below"): the chat lines hide while Home is up, and
  // "Continue the conversation" brings them back.
  _homeOnly(on) {
    this.messagesTarget.querySelectorAll(":scope > :not([data-permanent])").forEach((el) => { el.hidden = on })
    this.messagesTarget.classList.toggle("bg-gray-100", on) // the page-gray behind Home's white cards
  }

  // In a conversation: the chat lines and the typing box.
  _inChat() {
    this.homeTarget.classList.add("hidden")
    this._homeOnly(false)
    this.composerTarget.hidden = false
    this._setHeader(this._door === "studio" ? "studio" : "support")
  }

  // Their Ask AI conversation, opened from its row under Messages.
  resume() {
    this._msgsOpen = false
    this.msgsTarget.hidden = true
    this.messagesTarget.hidden = false
    this._paintAccent()
    this._inChat()
    this._paintTabs("messages")
    track("Ask AI Conversation Resumed", { turns: this.history.length })
    this._scroll()
  }

  // ── Composer ─────────────────────────────────────────────────────

  keydown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.send() }
  }

  grow() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 120) + "px"
  }

  async send() {
    const text = this.inputTarget.value.trim()
    if (!text || this.pending) return
    this.inputTarget.value = ""
    this.grow()
    return this._sendText(text)
  }

  // The pencil on a block Studio placed on the page (step 2, 2026-10-19): the
  // block is preselected, and its id rides with the next message, so "say
  // hard hats and vests" or "remove it" lands on that very block.
  _pickBlock(block) {
    if (!block || !block.id) return
    this._block = { id: String(block.id), kind: String(block.kind || ""), words: String(block.words || "").slice(0, 80) }
    const kind = (this.t.block_kinds || {})[this._block.kind] || this._block.kind
    const line = (this.t.block_picked || 'You picked the %{kind} "%{words}". Tell me the new words, a new place, or say "remove it".')
      .replace("%{kind}", kind.toLowerCase()).replace("%{words}", this._block.words)
    this._appendAi(line, "studio")
    this._scroll()
    this.inputTarget?.focus()
  }

  // A line sent as if typed (the composer, or the dot's "Put it here: …").
  async _sendText(text) {
    if (!text || this.pending) return
    this._inChat()
    this.history.push({ role: "user", content: text })
    this.history = this.history.slice(-MAX_HISTORY)
    this._appendUser(text)
    this._convLine("user", text)
    track("Ask AI Message Sent", { door: this._door || "none" })

    const thinking = this._appendThinking()
    this._setPending(true)
    try {
      const data = await this._post(this.chatUrlValue, { messages: this.history, door: this._door, context: this._context() })
      this._block = null // the picked block rides with one message only
      thinking.remove()
      if (!data) { this._appendAi(this.t.network_error); return }
      this._handle(data)
    } catch (err) {
      thinking.remove()
      this._appendAi(this.t.network_error)
    } finally {
      this._setPending(false)
      this._persist()
    }
  }

  _handle(data) {
    if (data.reply) {
      this.history.push({ role: "assistant", content: data.reply })
      this.history = this.history.slice(-MAX_HISTORY)
      this._appendAi(data.reply, this._tagFor(data.intent))
      this._convLine("assistant", data.reply, data.change_id ? { kind: "change", id: data.change_id } : null)
    }
    switch (data.intent) {
      case "studio":
        if (data.card_html) this._appendHtml(data.card_html)
        // The spot on the page itself (Phelipe 2026-10-17: "I should see where it's going to put it").
        if (data.preview && data.change_id) this._showGhost(data.change_id, data.preview)
        // Studio chose the place (Phelipe 2026-10-17: "it adds everything in the
        // same spot"): the dot lets them put it exactly where they want instead.
        if (data.preview && data.change_id && hasSpots(data.spot_kind || null)) this._appendButtons([{ act: "pick", label: this.t.pick_spot, kind: data.spot_kind || "" }])
        // A report form needs the paper form itself (Wave 1): one button opens the picker.
        if (data.wants_file) this._appendButtons([{ act: "attach", label: this.t.attach_cta }])
        // "Where exactly?" — they can draw the spot on the page (Phelipe 2026-10-17).
        if (data.offer_draw) {
          const btns = []
          // The dot (Phelipe 2026-10-17): click the exact place, when the page has places to pick.
          if (hasSpots(data.spot_kind || null)) btns.push({ act: "pick", label: this.t.pick_spot, kind: data.spot_kind || "" })
          btns.push({ act: "draw", label: this.t.draw_where })
          this._appendButtons(btns)
        }
        break
      case "support":
        this._appendSupportExtras(data)
        break
      case "feedback":
        this._appendFeedbackCard(data.feedback_draft)
        break
      case "person":
        this._handoff()
        break
      default:
        if (data.offer_person) this._appendButtons([{ act: "person", label: this.t.talk_btn }])
    }
    this._scroll()
  }

  _tagFor(intent) {
    if (intent === "studio") return "studio"
    if (intent === "support") return "support"
    // "Studio can't do this one yet…" inside the Studio flow is Studio talking.
    if (intent === "feedback" && this._door === "studio") return "studio"
    return null
  }

  // ── Draw where (Phelipe 2026-10-17) ───────────────────────────────
  // The Feedback Loop's pen, borrowed: the page gets the drawing layer, the
  // window steps aside, and the picture comes back here and goes to Studio
  // with the conversation so it can see the spot.

  startDraw() {
    track("Ask AI Draw Started", { door: this._door || "none" })
    this._appendAi(this.t.drawing_hint, "studio")
    window.dispatchEvent(new CustomEvent("instant-feedback:draw", { detail: { host: "studio" } }))
  }

  // ── Pick the spot (Phelipe 2026-10-17) ────────────────────────────
  // Optional, next to "Draw where": the dot runs over the page and snaps to a
  // real place; the window steps aside (unless docked beside the page) and the
  // spot comes back as the next message, carried as data the server applies.
  async startPick(kind = null) {
    if (this._picking || this.pending) return
    this._picking = true
    // One dot at a time: the earlier offers go, and the card this one replaces
    // loses its buttons once the new one is here.
    this.messagesTarget.querySelectorAll('[data-studio-act="pick"]').forEach((b) => { const row = b.parentElement; b.remove(); if (row && !row.children.length) row.remove() })
    const before = [...this.messagesTarget.querySelectorAll('[data-studio-act="confirm"], [data-studio-act="reject"]')]
    const docked = this._wantsDock()
    if (!docked) { this._pickAside = true; this.close() }
    let spot = null
    try {
      spot = await pickSpot(this.t, { kinds: kind ? [kind] : null })
    } finally {
      this._picking = false
      if (!docked) { this._pickAside = false; this._show() }
    }
    track("Ask AI Spot Picked", { door: this._door || "none", picked: !!spot, kind: spot?.kind || "none" })
    if (!spot) { this._appendAi(this.t.draw_cancelled, "studio"); this._scroll(); return }
    // The card the dot was pressed under, if one is still waiting: the server moves that very change.
    const replaces = [...before].reverse().find((b) => b.dataset.studioAct === "confirm")?.dataset.id
    if (replaces) spot.replaces = replaces
    this._spot = spot
    const line = (this.t.spot_put_here || "Put it here: %{where}").replace("%{where}", spot.label || "")
    try { await this._sendText(line) } finally { this._spot = null }
    const after = [...this.messagesTarget.querySelectorAll('[data-studio-act="confirm"], [data-studio-act="reject"]')]
    if (after.length > before.length) before.forEach((b) => b.remove())
  }

  // The dot for a request Studio can't build: the spot's words go into the
  // request itself ("Where on the page: after the Date column"), and ride
  // along when it is sent or opened in the Feedback Loop form.
  async feedbackPick(btn) {
    const card = btn.closest("[data-feedback-card]")
    if (!card || this._picking) return
    this._picking = true
    const docked = this._wantsDock()
    if (!docked) { this._pickAside = true; this.close() }
    let spot = null
    try {
      spot = await pickSpot(this.t)
    } finally {
      this._picking = false
      if (!docked) { this._pickAside = false; this._show() }
    }
    track("Ask AI Spot Picked", { door: this._door || "none", picked: !!spot, kind: spot?.kind || "none", for: "feedback" })
    if (!spot) return
    const line = (this.t.spot_where || "Where on the page: %{where}").replace("%{where}", spot.label || "")
    const problem = card.querySelector("[data-fb-problem]")
    if (problem) {
      problem.querySelector("[data-fb-spot]")?.remove()
      const row = document.createElement("div")
      row.setAttribute("data-fb-spot", "1")
      row.className = "mt-1 font-medium text-gray-900"
      // A leading newline keeps the request's text and the spot apart when read back.
      row.textContent = "\n" + line
      problem.appendChild(row)
    }
    btn.textContent = line
    this._scroll()
  }

  async _sendImage(dataUrl) {
    if (!dataUrl) return
    const line = this.t.drawn || "Picture of the spot attached"
    this.history.push({ role: "user", content: line })
    this.history = this.history.slice(-MAX_HISTORY)
    this._appendUser(line)
    this._convLine("user", line)
    const bubble = this.messagesTarget.lastElementChild?.firstElementChild
    if (bubble) {
      const img = document.createElement("img")
      img.src = dataUrl
      img.alt = ""
      img.className = "mt-1.5 rounded-lg max-h-28 ring-1 ring-white/40"
      bubble.appendChild(img)
    }
    const thinking = this._appendThinking()
    this._setPending(true)
    try {
      const data = await this._post(this.chatUrlValue, { messages: this.history, door: this._door, context: this._context(), image: dataUrl })
      thinking.remove()
      if (!data) { this._appendAi(this.t.network_error); return }
      this._handle(data)
    } catch (err) {
      thinking.remove()
      this._appendAi(this.t.network_error)
    } finally {
      this._setPending(false)
      this._persist()
    }
  }

  // ── A paper form (PDF or photo) → a report form (Wave 1, 2026-10-17) ──
  // The file goes straight to /studio/forms/extract; the answer is the usual
  // Studio preview card (Approve creates the report type, or sends it for review).

  pickFile() {
    this._fileInput()?.click()
  }

  _fileInput() {
    return this.element.querySelector('input[type="file"][data-studio-chat-target="file"]')
  }

  async fileChosen(e) {
    const input = e.currentTarget
    const file = input.files && input.files[0]
    input.value = ""
    if (!file) return
    if (!this._door) { this._door = "studio"; this._paintAccent() }
    const line = `\u{1F4CE} ${file.name}`
    this.history.push({ role: "user", content: line })
    this.history = this.history.slice(-MAX_HISTORY)
    this._appendUser(line)
    track("Ask AI Form Attached", { size_kb: Math.round(file.size / 1024), type: file.type || "unknown" })

    const thinking = this._appendThinking()
    this._setPending(true)
    try {
      const body = new FormData()
      body.append("file", file)
      const res = await fetch(input.dataset.url, {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body
      }).catch(() => null)
      thinking.remove()
      const data = res ? await res.json().catch(() => null) : null
      if (!data) { this._appendAi(this.t.network_error); return }
      this._handle(data)
    } catch (err) {
      thinking.remove()
      this._appendAi(this.t.network_error)
    } finally {
      this._setPending(false)
      this._persist()
    }
  }

  // ── Clicks inside the conversation (cards, buttons) ──────────────

  _onMessagesClick(e) {
    const btn = e.target.closest("[data-studio-act]")
    if (!btn) return
    e.preventDefault()
    const act = btn.dataset.studioAct
    const id = btn.dataset.id
    switch (act) {
      case "person": return this.person()
      case "helped": return this._helped(btn)
      case "attach": return this.pickFile()
      case "draw": return this.startDraw()
      case "pick": return this.startPick(btn.dataset.kind || null)
      case "article": return this._openArticle(btn.dataset.embed, btn.getAttribute("href"), btn.dataset.title)
      case "report": return this._report(btn)
      case "confirm": return this._changeAction(btn, id, "confirm")
      case "reject": return this._changeAction(btn, id, "reject")
      case "undo": return this._changeAction(btn, id, "undo")
      case "fb-draft": return this._createRequest(btn, true)
      // Phelipe 2026-10-17: Record starts the screen recording right away and
      // lands in the Feedback Loop form when it stops; Open goes to that form's
      // first screen. Both carry the card's title, words and kind.
      case "fb-record": this._fbCard = btn.closest("[data-feedback-card]"); return this._hostFeedback({ ...this._fbCardOpts(btn), mode: "record" }, "chat")
      case "fb-open": this._fbCard = btn.closest("[data-feedback-card]"); return this._hostFeedback(this._fbCardOpts(btn), "chat")
      case "fb-send": return this._createRequest(btn, false)
      case "fb-cancel": btn.closest("[data-feedback-card]")?.remove(); return
      // The spot on the page, marked for the team (Phelipe 2026-10-17: "can't build
      // this" still says exactly where it was wanted).
      case "fb-pick": return this.feedbackPick(btn)
      // The request a brought-back conversation ended with: the details panel, inside the window.
      case "open-request": return this.openThread(+id, "", false, "chat")
    }
  }

  // Approve / Send for review / Not this / Undo on a Studio card.
  async _changeAction(btn, id, action) {
    const card = btn.closest("[data-change-id]")
    card?.querySelectorAll("button").forEach((b) => { b.disabled = true })
    const data = await this._post(`${this.changeUrlValue}/${id}/${action}`, {})
    if (!data || data.ok === false) {
      card?.querySelectorAll("button").forEach((b) => { b.disabled = false })
      showToast((data && data.error) || this.t.network_error, "error")
      return
    }
    if (data.card_html && card) {
      const tpl = document.createElement("template")
      tpl.innerHTML = data.card_html.trim()
      const fresh = tpl.content.firstElementChild
      if (fresh) card.replaceWith(fresh)
    }
    // Decided either way: the ghost on the page goes — and the page redraws
    // what it can on its own (2026-10-17: no full reload).
    if (action === "confirm" || action === "reject") this._clearGhost(id)
    if (data.live && action !== "reject") this._applyLive(id, data.live)
    if (action === "confirm" && data.reply) {
      this._appendAi(data.reply, "studio")
      this._convLine("assistant", data.reply)
      if (data.status === "live") this._appendLink(this.feedbackPageUrlValue, this.t.see_changes)
    } else if (action === "undo") {
      this._appendAi(this.t.undone, "studio")
      this._convLine("assistant", this.t.undone)
    }
    // Approve (or Send for review) and Undo end the conversation (Phelipe 2026-10-17); Not this keeps it going.
    if (action === "confirm" || action === "undo") {
      this._convClose({ outcome_type: "StudioChange", outcome_id: +id, outcome_label: this._changeStatusLabel(action === "undo" ? "undone" : data.status) })
    }
    this._scroll()
    this._persist()
  }

  // ── The change, drawn on the page before Approve (Phelipe 2026-10-17) ──
  // A dashed purple box where the new field will sit in the form on this
  // page, or a dashed column in the table — so the person sees the spot and
  // can say "no, next to Notes" before anything is live. One ghost at a time;
  // Approve and Not this remove it.

  _showGhost(changeId, preview) {
    this._clearGhost()
    try {
      const tag = this.t.preview_tag || "Studio preview"
      if (preview.kind === "record_field") {
        // A field on the record's own form (step 3): the control itself, dashed, where it will sit;
        // a removal outlines the field in red. Nothing to draw when the form isn't on this page.
        const holder = document.querySelector('[data-controller~="custom-fields"]')
        if (!holder) return
        const root = holder.closest("form, [data-custom-fields-root]") || holder.parentElement
        if (preview.action === "remove_field" || preview.action === "edit_field" || preview.action === "move_field") {
          const cur = preview.id ? root.querySelector(`[data-custom-field="${CSS.escape(String(preview.id))}"]`) : null
          if (cur && preview.action === "remove_field") {
            cur.setAttribute("data-studio-ghost", changeId); cur.setAttribute("data-studio-ghost-keep", "1")
            cur.style.cssText = "outline:2px dashed #ef4444;outline-offset:4px;border-radius:8px;opacity:.55;text-decoration:line-through"
            cur.scrollIntoView({ block: "center", behavior: "smooth" })
            return
          }
          if (cur) { cur.style.cssText = "opacity:.45"; cur.setAttribute("data-studio-ghost-dim", changeId) }
        }
        if (!preview.html) return
        const wrap = document.createElement("div")
        wrap.innerHTML = preview.html
        const ghost = wrap.firstElementChild
        if (!ghost) return
        ghost.setAttribute("data-studio-ghost", changeId)
        ghost.removeAttribute("data-custom-field")
        ghost.style.cssText = "border:2px dashed #a855f7;background:#faf5ff;border-radius:8px;padding:8px 10px"
        ghost.querySelectorAll("input, select, textarea").forEach((n) => { n.disabled = true })
        ghost.insertAdjacentHTML("beforeend", `<div class="text-[13px]" style="color:#7e22ce">${this._esc(tag)}</div>`)
        placeField(ghost, preview.anchor || {}, { root, holder })
        ghost.scrollIntoView({ block: "center", behavior: "smooth" })
      } else if (preview.kind === "field") {
        const found = this._ghostSection(preview)
        if (!found) return
        const { host, section } = found
        const wanted = (preview.section_label || "").trim()
        let grid = section?.querySelector(".grid")
        if (!grid) {
          // A section with no fields yet — or an empty place on a built-in form,
          // which has no heading of its own: the whole block is new, so draw it too.
          const block = document.createElement("div")
          block.className = "border-t border-gray-200 pt-4"
          block.setAttribute("data-studio-ghost", changeId)
          const heading = preview.anchor ? "" : `<h3 class="text-sm font-semibold text-gray-700 mb-3">${this._esc(wanted || "General")}</h3>`
          block.innerHTML = `${heading}<div class="grid grid-cols-1 sm:grid-cols-6 gap-4"></div>`
          host.appendChild(block)
          grid = block.querySelector(".grid")
        }
        const span = { third: "sm:col-span-2", half: "sm:col-span-3", full: "sm:col-span-6" }[preview.width] || "sm:col-span-3"
        const ghost = document.createElement("div")
        ghost.className = span
        ghost.setAttribute("data-studio-ghost", changeId)
        ghost.innerHTML = `<div class="rounded-lg px-3 py-2.5" style="border:2px dashed #a855f7;background:#faf5ff">
          <div class="text-[13px] font-semibold text-gray-800">${this._esc(preview.label || "")}</div>
          <div class="text-[13px] text-gray-500">${this._esc(preview.type_word || "")} · <span style="color:#7e22ce">${this._esc(tag)}</span></div>
        </div>`
        const fields = [...grid.children].filter((c) => !c.hasAttribute("data-studio-ghost"))
        const at = preview.position == null || preview.position === "" ? fields.length : Math.min(+preview.position, fields.length)
        if (at < fields.length) grid.insertBefore(ghost, fields[at]); else grid.appendChild(ghost)
        ghost.scrollIntoView({ block: "center", behavior: "smooth" })
      } else if (preview.kind === "move" || preview.kind === "remove") {
        // The field itself, found by its label: outlined red to go, or a dashed
        // copy where it will sit.
        // The whole report form: a field can sit in any of the places on it.
        const host = document.querySelector('[data-controller~="report-form"]') || document.querySelector('[data-report-form-target="formTemplateFields"]')
        if (!host) return
        const wanted = (preview.label || "").trim().toLowerCase()
        const wrap = [...host.querySelectorAll("[data-section-key] .grid > div")].find((d) => (d.querySelector("label")?.textContent || "").replace(/\*/g, "").trim().toLowerCase() === wanted)
        if (!wrap) return
        if (preview.kind === "remove") {
          wrap.setAttribute("data-studio-ghost", changeId)
          wrap.setAttribute("data-studio-ghost-keep", "1")
          wrap.style.cssText = "outline:2px dashed #ef4444;outline-offset:4px;border-radius:8px;opacity:.55;text-decoration:line-through"
          wrap.scrollIntoView({ block: "center", behavior: "smooth" })
          return
        }
        const section = this._ghostSection(preview)?.section || wrap.closest("[data-section-key]")
        const grid = section?.querySelector(".grid")
        if (!grid) return
        const ghost = document.createElement("div")
        ghost.className = wrap.className
        ghost.setAttribute("data-studio-ghost", changeId)
        ghost.innerHTML = `<div class="rounded-lg px-3 py-2.5" style="border:2px dashed #a855f7;background:#faf5ff">
          <div class="text-[13px] font-semibold text-gray-800">${this._esc(preview.label || "")}</div>
          <div class="text-[13px] text-gray-500"><span style="color:#7e22ce">${this._esc(tag)}</span></div>
        </div>`
        const fields = [...grid.children].filter((c) => !c.hasAttribute("data-studio-ghost") && c !== wrap)
        const at = preview.position == null || preview.position === "" ? fields.length : Math.min(+preview.position, fields.length)
        if (at < fields.length) grid.insertBefore(ghost, fields[at]); else grid.appendChild(ghost)
        wrap.style.cssText = "opacity:.45"
        wrap.setAttribute("data-studio-ghost-dim", changeId)
        ghost.scrollIntoView({ block: "center", behavior: "smooth" })
      } else if (preview.kind === "block") {
        // A note, a button or a card on the page (2026-10-17): the dashed block
        // at its place; the block it edits, moves or removes is marked.
        const main = document.querySelector("main") || document.body
        const top = main.querySelector('[data-page-blocks-target="top"]')
        const bottom = main.querySelector('[data-page-blocks-target="bottom"]')
        const existing = preview.id ? main.querySelector(`[data-page-block="${CSS.escape(String(preview.id))}"]`) : null
        if (preview.action === "remove_block") {
          if (!existing) return
          existing.setAttribute("data-studio-ghost", changeId)
          existing.setAttribute("data-studio-ghost-keep", "1")
          existing.style.cssText = "outline:2px dashed #ef4444;outline-offset:4px;border-radius:8px;opacity:.55;text-decoration:line-through"
          existing.scrollIntoView({ block: "center", behavior: "smooth" })
          return
        }
        if (!preview.html) return
        const wrap = document.createElement("div")
        wrap.innerHTML = preview.html
        const ghost = wrap.firstElementChild
        if (!ghost) return
        ghost.setAttribute("data-studio-ghost", changeId)
        ghost.removeAttribute("data-page-block")
        ghost.style.cssText = "outline:2px dashed #a855f7;outline-offset:3px;border-radius:8px"
        if (ghost.tagName === "DIV") ghost.insertAdjacentHTML("beforeend", `<div class="text-[13px]" style="color:#7e22ce">${this._esc(tag)}</div>`)
        else ghost.title = tag
        if (existing) { existing.style.cssText = "opacity:.45"; existing.setAttribute("data-studio-ghost-dim", changeId) }
        if (existing && preview.action === "edit_block") existing.parentNode.insertBefore(ghost, existing.nextSibling)
        else placeBlock(ghost, preview.anchor || {}, { top, bottom, root: main })
        ghost.scrollIntoView({ block: "center", behavior: "smooth" })
      } else if (preview.kind === "columns") {
        const table = document.querySelector('[data-reports-target="table"]')
        const head = table?.querySelector("thead tr")
        if (!head) return
        const order = Array.isArray(preview.columns_after) ? preview.columns_after : []
        const before = Array.isArray(preview.columns_before) ? preview.columns_before : []
        const added = Array.isArray(preview.added) ? preview.added : []
        const removed = Array.isArray(preview.removed) ? preview.removed : []
        const moved = Array.isArray(preview.moved) ? preview.moved : []
        // A column going away, or moving: the real header and cells get marked.
        // The page's own columns (marked ones included) — never the dashed copies.
        const real = (cells) => cells.filter((c) => !c.hasAttribute("data-studio-ghost") || c.hasAttribute("data-studio-ghost-keep"))
        const mark = (key, css) => {
          const idx = before.indexOf(key)
          if (idx < 0) return
          const th = real([...head.children])[idx]
          if (!th) return
          th.setAttribute("data-studio-ghost", changeId); th.setAttribute("data-studio-ghost-keep", "1"); th.style.cssText = css
          ;[...(table.querySelectorAll("tbody tr"))].slice(0, 40).forEach((tr) => {
            const td = real([...tr.children])[idx]
            if (td) { td.setAttribute("data-studio-ghost", changeId); td.setAttribute("data-studio-ghost-keep", "1"); td.style.cssText = css }
          })
        }
        removed.forEach((key) => mark(key, "outline:2px dashed #ef4444;outline-offset:-2px;opacity:.5;text-decoration:line-through"))
        moved.forEach((key) => mark(key, "opacity:.4"))
        added.concat(moved).forEach((key) => {
          const at = order.indexOf(key)
          if (at < 0) return
          const th = document.createElement("th")
          th.setAttribute("data-studio-ghost", changeId)
          th.className = "px-2 py-2 text-[13px] font-semibold whitespace-nowrap"
          th.style.cssText = "border:2px dashed #a855f7;background:#faf5ff;color:#7e22ce"
          th.title = tag
          th.textContent = (preview.labels || {})[key] || key
          const heads = real([...head.children])
          if (at < heads.length) head.insertBefore(th, heads[at]); else head.appendChild(th)
          ;[...(table.querySelectorAll("tbody tr"))].slice(0, 40).forEach((tr) => {
            const td = document.createElement("td")
            td.setAttribute("data-studio-ghost", changeId)
            td.style.cssText = "border-left:2px dashed #a855f7;border-right:2px dashed #a855f7;background:#faf5ff"
            const cells = real([...tr.children])
            if (at < cells.length) tr.insertBefore(td, cells[at]); else tr.appendChild(td)
          })
        })
        head.querySelector("[data-studio-ghost]")?.scrollIntoView({ block: "center", behavior: "smooth" })
      }
    } catch (e) { /* a page without the spot: the card still says where */ }
  }

  // After Approve or Undo (Phelipe 2026-10-17: no full reload): the report form
  // takes its fresh schema and redraws; a block goes live where its ghost stood,
  // changes, or leaves. The card's "reload" line goes when the page handled it.
  _applyLive(changeId, live) {
    let handled = false
    try {
      if (live.kind === "form_template") {
        if (document.querySelector('[data-controller~="report-form"]')) {
          window.dispatchEvent(new CustomEvent("studio:form-template", { detail: live }))
          handled = true
        }
      } else if (live.kind === "record_field") {
        // The record's form on this page gets the field (or loses it) right away.
        const holder = document.querySelector('[data-controller~="custom-fields"]')
        if (holder) {
          const root = holder.closest("form, [data-custom-fields-root]") || holder.parentElement
          root.querySelectorAll(`[data-custom-field="${CSS.escape(String(live.id))}"]`).forEach((n) => n.remove())
          if (live.exists && live.html) {
            const wrap = document.createElement("div")
            wrap.innerHTML = live.html
            const el = wrap.firstElementChild
            if (el) placeField(el, live.anchor || {}, { root, holder })
          }
          handled = true
        }
      } else if (live.kind === "table_columns") {
        // The table on this page redraws with its new columns (step 2, 2026-10-19):
        // the headers come from a fresh copy of the page, the rows from the page's
        // own controller, which draws cells in the order its columns value gives.
        const table = document.querySelector(`[data-studio-table="${CSS.escape(String(live.page))}"]`)
        if (table) {
          const host = table.closest("[data-controller]")
          if (host) [...host.attributes].filter((a) => /^data-[a-z-]+-columns-value$/.test(a.name)).forEach((a) => host.setAttribute(a.name, JSON.stringify(live.columns || [])))
          fetch(location.href, { headers: { Accept: "text/html" }, credentials: "same-origin" }).then((r) => r.text()).then((html) => {
            const fresh = new DOMParser().parseFromString(html, "text/html").querySelector(`[data-studio-table="${CSS.escape(String(live.page))}"]`)
            const head = fresh?.querySelector("thead"), mine = table.querySelector("thead")
            if (head && mine) mine.replaceWith(head)
            window.dispatchEvent(new CustomEvent("studio:table-columns", { detail: live }))
          }).catch(() => {})
          handled = true
        }
      } else if (live.kind === "page_block") {
        const main = document.querySelector("main") || document.body
        main.querySelectorAll(`[data-page-block="${CSS.escape(String(live.id))}"]`).forEach((n) => n.remove())
        if (live.exists && live.html) {
          const wrap = document.createElement("div")
          wrap.innerHTML = live.html
          const el = wrap.firstElementChild
          if (el) {
            placeBlock(el, live.anchor || {}, { top: main.querySelector('[data-page-blocks-target="top"]'), bottom: main.querySelector('[data-page-blocks-target="bottom"]'), root: main })
            el.dispatchEvent(new CustomEvent("page-blocks:added", { bubbles: true }))
          }
        }
        handled = true
      }
    } catch (e) { handled = false }
    if (handled) this.messagesTarget.querySelectorAll(`[data-change-id="${changeId}"] [data-studio-reload]`).forEach((n) => n.remove())
  }

  // The section a field goes in: by its key first (a place on a built-in form
  // has no heading), then its anchor box when the place is still empty, then
  // by its heading on the form's template fields.
  _ghostSection(preview) {
    const form = document.querySelector('[data-controller~="report-form"]')
    const key = preview.section_key || ""
    const byKey = form && key ? form.querySelector(`[data-section-key="${CSS.escape(key)}"]`) : null
    if (byKey) return { host: byKey.parentElement, section: byKey }
    const box = form && key ? form.querySelector(`[data-studio-anchor="${CSS.escape(key)}"]`) : null
    if (box) return { host: box, section: box.querySelector("[data-section-key]") }
    const host = document.querySelector('[data-report-form-target="formTemplateFields"]')
    if (!host) return null
    const sections = [...host.querySelectorAll(":scope > div")]
    const wanted = (preview.section_label || "").trim()
    return { host, section: sections.find((d) => (d.querySelector("h3")?.textContent || "").trim() === wanted) || sections[0] }
  }

  _clearGhost(changeId = null) {
    const sel = changeId ? `[data-studio-ghost="${changeId}"]` : "[data-studio-ghost]"
    document.querySelectorAll(sel).forEach((el) => {
      // A real field only marked (to go, or dimmed while its copy shows the new place) stays.
      if (el.hasAttribute("data-studio-ghost-keep")) { el.removeAttribute("data-studio-ghost"); el.removeAttribute("data-studio-ghost-keep"); el.style.cssText = ""; return }
      el.remove()
    })
    const dimSel = changeId ? `[data-studio-ghost-dim="${changeId}"]` : "[data-studio-ghost-dim]"
    document.querySelectorAll(dimSel).forEach((el) => { el.style.cssText = ""; el.removeAttribute("data-studio-ghost-dim") })
  }

  // ── Speak instead of typing (Phelipe 2026-10-17) ───────────────────
  // The microphone next to the paperclip: press to record, press again to
  // stop; the recording goes to the server, Gemini writes the words down,
  // and they land in the box for the person to read and send — like the
  // Feedback Loop's "Written from what you said".

  async toggleDictation() {
    if (this._dict === "busy") return
    if (this._dict === "recording") return this._stopDictation()
    if (!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)) { showToast(this.t.dictate_unsupported || "This browser can't record here.", "error"); return }
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      showToast(this.t.mic_denied || "Allow the microphone to speak your message.", "error")
      return
    }
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "")
    this._dictStream = stream
    this._dictChunks = []
    this._dictMime = mime || "audio/webm"
    try {
      this._dictRec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop())
      showToast(this.t.dictate_unsupported || "This browser can't record here.", "error")
      return
    }
    this._dictRec.ondataavailable = (e) => { if (e.data.size) this._dictChunks.push(e.data) }
    this._dictRec.onstop = () => this._dictationDone()
    this._dictRec.start(250)
    this._dictStart = Date.now()
    this._dict = "recording"
    this._paintMic()
    this._dictTimer = setInterval(() => {
      const s = Math.floor((Date.now() - this._dictStart) / 1000)
      if (this.hasMicTimerTarget) this.micTimerTarget.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
      if (s >= DICTATION_MAX_SECONDS) this._stopDictation()
    }, 250)
    track("Studio Dictation Started", { door: this._door || "none" })
  }

  _stopDictation() {
    if (this._dict !== "recording") return
    clearInterval(this._dictTimer)
    this._dict = "busy"
    this._paintMic()
    try { this._dictRec.stop() } catch (e) { this._dictationDone() }
  }

  async _dictationDone() {
    this._dictStream?.getTracks().forEach((t) => t.stop())
    const blob = new Blob(this._dictChunks, { type: this._dictMime })
    const seconds = Math.round((Date.now() - this._dictStart) / 1000)
    this._dictChunks = []
    const finish = () => { this._dict = "idle"; this._paintMic() }
    if (!blob.size || seconds < 1) return finish()
    const fd = new FormData()
    fd.append("audio", blob, `dictation.${this._dictMime.includes("mp4") ? "m4a" : "webm"}`)
    const res = await fetch(this.micBtnTarget.dataset.url, {
      method: "POST", credentials: "same-origin", body: fd,
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
    }).catch(() => null)
    const data = res ? await res.json().catch(() => null) : null
    finish()
    if (!data?.ok || !data.text) {
      showToast(data?.error === "nothing_heard" ? (this.t.dictate_nothing || "I didn't hear anything.") : (this.t.dictate_failed || "I couldn't write that down. Try again."), "error")
      return
    }
    const cur = this.inputTarget.value.trim()
    this.inputTarget.value = cur ? `${cur} ${data.text}` : data.text
    this.grow()
    this.inputTarget.focus()
    this.inputTarget.setSelectionRange(this.inputTarget.value.length, this.inputTarget.value.length)
    track("Studio Message Dictated", { seconds, door: this._door || "none" })
  }

  _paintMic() {
    if (!this.hasMicBtnTarget) return
    const btn = this.micBtnTarget
    const state = this._dict || "idle"
    btn.querySelectorAll("[data-mic-state]").forEach((el) => {
      const on = el.dataset.micState === state
      el.classList.toggle("hidden", !on)
      if (el.dataset.micState === "recording") el.classList.toggle("flex", on)
    })
    btn.classList.toggle("w-10", state !== "recording")
    btn.style.width = state === "recording" ? "auto" : ""
    btn.style.paddingLeft = btn.style.paddingRight = state === "recording" ? "8px" : ""
    const label = state === "recording" ? btn.dataset.labelStop : btn.dataset.labelStart
    btn.title = label
    btn.setAttribute("aria-label", label)
  }

  // ── Support extras ───────────────────────────────────────────────

  // Under an answer (mock v32): the help article it came from, then
  // "That helped", "Talk to the team" and "Send as feedback" — nobody retypes.
  _appendSupportExtras(data) {
    const articles = Array.isArray(data.articles) ? data.articles : []
    const box = document.createElement("div")
    box.className = "space-y-2"
    articles.slice(0, 2).forEach((a) => {
      const link = document.createElement("a")
      link.href = a.url
      link.className = "flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
      link.innerHTML = `<span aria-hidden="true">\u{1F4C4}</span><span class="min-w-0 truncate">${this._esc(this.t.from_help_center || "From the help center")}: <b class="text-brand-dark font-semibold">${this._esc(a.title)}</b></span>`
      if (a.embed_url) { link.dataset.studioAct = "article"; link.dataset.embed = a.embed_url; link.dataset.title = a.title } else { link.target = "_blank"; link.rel = "noopener" }
      box.appendChild(link)
    })
    const pills = document.createElement("div")
    pills.className = "flex flex-wrap gap-1.5"
    pills.dataset.answerPills = "1"
    const pill = (act, label, go) => {
      const b = document.createElement("button")
      b.type = "button"
      b.dataset.studioAct = act
      b.className = go
        ? "inline-flex items-center gap-1.5 text-[13px] rounded-full px-3 py-1.5 border border-brand/30 bg-brand/5 text-brand-dark hover:bg-brand/10 cursor-pointer disabled:opacity-60"
        : "inline-flex items-center gap-1.5 text-[13px] rounded-full px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer disabled:opacity-60"
      b.textContent = label
      return b
    }
    pills.appendChild(pill("helped", this.t.that_helped || "That helped", false))
    pills.appendChild(pill("person", this.t.talk_team || "Talk to the team", true))
    pills.appendChild(pill("report", this.t.send_feedback || "Send as feedback", true))
    box.appendChild(pills)
    this.messagesTarget.appendChild(box)
    this._scroll()
  }

  // "That helped": a thank-you, the pills go quiet, and a count for us.
  _helped(btn) {
    btn.closest("[data-answer-pills]")?.querySelectorAll("button").forEach((b) => { b.disabled = true })
    this._appendAi(this.t.helped_thanks || "Glad it helped!")
    track("Ask AI Answer Rated", { helpful: true })
  }

  // "Report it as an issue" after a support answer: the Instant Feedback
  // panel opens on its first screen (record, draw or write), with the words
  // they typed already in the box and the request marked as from Studio.
  _report() {
    const last = [...this.history].reverse().find((m) => m.role === "user")
    const text = last ? last.content : ""
    this._hostFeedback({ prefill: text, source: "studio" }, "chat")
    track("Ask AI Issue Reported", { draft: false, from: "support" })
  }

  // ── The Feedback Loop request Studio writes ──────────────────────

  _appendFeedbackCard(draft) {
    if (!draft) return
    const wrap = this._row()
    const card = document.createElement("div")
    card.setAttribute("data-feedback-card", "1")
    card.className = "ml-8 rounded-xl border border-amber-200 overflow-hidden bg-white"
    const kinds = this.t.kinds || {}
    card.innerHTML = `
      <div class="bg-amber-50 px-3 py-2 flex items-center justify-between gap-2">
        <span class="text-[13px] font-semibold text-amber-800">${this._esc(this.t.door_feedback || "Feedback Loop")}</span>
        <span class="text-[13px] text-gray-500">${this._esc(kinds[draft.kind] || draft.kind || "")}</span>
      </div>
      <div class="px-3 py-2.5 space-y-1 text-[13px]">
        <div class="text-[14px] font-semibold text-gray-900" data-fb-title>${this._esc(draft.title || "")}</div>
        <div class="text-gray-700 leading-relaxed" data-fb-problem>${this._esc(draft.problem || "")}</div>
      </div>
      <div class="px-3 pb-3 space-y-2">
        <div class="flex flex-wrap gap-2">
          <button type="button" data-studio-act="fb-record" data-kind="${this._esc(draft.kind || "feature")}" class="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-red-700 hover:bg-red-800 text-white text-[14px] font-semibold cursor-pointer disabled:opacity-60"><span class="w-2 h-2 rounded-full bg-white"></span>${this._esc(this.t.fb_record || "Record my screen")}</button>
          <button type="button" data-studio-act="fb-send" data-kind="${this._esc(draft.kind || "feature")}" class="px-3 h-9 rounded-lg border border-gray-300 bg-white text-gray-700 text-[14px] font-medium hover:bg-gray-50 cursor-pointer disabled:opacity-60">${this._esc(this.t.send_without || "Send without one")}</button>
          <button type="button" data-studio-act="fb-cancel" class="px-2 h-9 text-[13px] text-gray-500 hover:text-gray-800 cursor-pointer">${this._esc(this.t.not_now || "Not now")}</button>
        </div>
        ${hasSpots() ? `<button type="button" data-studio-act="fb-pick" class="w-full inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-[14px] font-medium cursor-pointer">${this._esc(this.t.pick_spot || "Pick the spot")}</button>` : ""}
        <button type="button" data-studio-act="fb-open" data-kind="${this._esc(draft.kind || "feature")}" class="w-full inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-[14px] font-medium cursor-pointer">${this._esc(this.t.fb_open || "Open the Feedback Loop")} ›</button>
      </div>`
    wrap.appendChild(card)
    this.messagesTarget.appendChild(wrap)
    this._scroll()
  }

  // What the chat's card hands to the Feedback Loop form.
  _fbCardOpts(btn) {
    const card = btn.closest("[data-feedback-card]")
    return {
      source: "studio",
      prefill: card?.querySelector("[data-fb-problem]")?.textContent.trim() || "",
      prefill_title: card?.querySelector("[data-fb-title]")?.textContent.trim() || "",
      prefill_kind: btn.dataset.kind || "feature"
    }
  }

  async _createRequest(btn, asDraft) {
    const card = btn.closest("[data-feedback-card]")
    if (!card) return
    card.querySelectorAll("button").forEach((b) => { b.disabled = true })
    const title = card.querySelector("[data-fb-title]")?.textContent.trim() || ""
    const problem = card.querySelector("[data-fb-problem]")?.textContent.trim() || ""
    const transcript = this.history.slice(-6).map((m) => `${m.role === "assistant" ? "AI" : "User"}: ${m.content}`).join("\n")
    const body = {
      source: "studio", draft: asDraft, kind: btn.dataset.kind || "feature",
      title, problem: asDraft ? problem : `${problem}\n\n— ${transcript}`.slice(0, 4000),
      context: Object.assign(this._context(), { studio: true })
    }
    const data = await this._post(this.feedbackUrlValue, body)
    if (!data || !data.request) {
      card.querySelectorAll("button").forEach((b) => { b.disabled = false })
      showToast((data && data.error) || this.t.network_error, "error")
      return
    }
    track("Ask AI Issue Reported", { draft: asDraft, from: "studio" })
    this._stampCard(card, asDraft ? this.t.fb_draft_stamp : this._sentStamp(data.request))
    if (asDraft) {
      showToast(this.t.request_saved_draft)
      this._fbCard = card
      this._hostFeedback({ resume: data.request.id }, "chat")
    } else {
      this._appendAi(this.t.request_sent)
      this._appendLink(this.feedbackPageUrlValue.replace(/\?tab=studio$/, ""), this.t.see_feedback)
      this._convLine("assistant", this.t.request_sent)
      this._convClose({ outcome_type: "CustomerRequest", outcome_id: data.request.id, outcome_label: this._sentStamp(data.request) })
      this._scroll()
    }
    this._persist()
  }

  // ── A help article inside the chat (Phelipe 2026-10-16, like Intercom) ──

  // Articles open big (Phelipe 2026-10-17, like Intercom's expanded article);
  // the button in the article bar shrinks it back to the bubble and back.
  _articleBig(on) {
    // Docked beside the page the window is already full height: nothing to grow.
    if (this._docked) { this._articleIsBig = false; return }
    const p = this.panelTarget
    if (this._panelSize === undefined) this._panelSize = { width: p.style.width, height: p.style.height }
    // A bit under full size (Phelipe 2026-10-17: "a tiny bit smaller").
    p.style.width = on ? "min(880px, calc(100vw - 48px))" : this._panelSize.width
    p.style.height = on ? "calc(100vh - 136px)" : this._panelSize.height
    this._articleIsBig = on
    if (!this.hasArticleSizeTarget) return
    const btn = this.articleSizeTarget
    btn.querySelector('[data-size-icon="smaller"]').classList.toggle("hidden", !on)
    btn.querySelector('[data-size-icon="bigger"]').classList.toggle("hidden", on)
    const label = on ? btn.dataset.labelSmaller : btn.dataset.labelBigger
    btn.title = label
    btn.setAttribute("aria-label", label)
  }

  // While an article is open only its own top bar shows (Phelipe 2026-10-17):
  // the blue header and the tab bar step aside, and come back with Back.
  _articleChrome(reading) {
    if (this.hasHeaderTarget) this.headerTarget.hidden = reading
    if (this.hasTabsTarget) this.tabsTarget.hidden = reading
  }

  toggleArticleSize() {
    this._articleBig(!this._articleIsBig)
    track("Ask AI Article Resized", { size: this._articleIsBig ? "big" : "small" })
  }

  async _openArticle(embedUrl, href, title) {
    if (!embedUrl) return
    this._articleOpen = true
    this._articleBig(true)
    this._articleChrome(true)
    // From the Help tab it goes back to the list; from the chat, to the chat.
    this.articleTarget.querySelectorAll("[data-article-back-label]").forEach((el) => {
      el.textContent = this._helpOpen ? (this.t.back || "Back") : (this.t.article_back || "Back to chat")
    })
    this.articleTitleTarget.textContent = title || ""
    const out = href || embedUrl.replace(/\/embed(\?.*)?$/, "")
    this.articleLinkTargets.forEach((a) => { a.href = out })
    this.articleBodyTarget.innerHTML = `<p class="text-[13px] text-gray-500">${this._esc(this.t.article_loading || "Loading…")}</p>`
    this.messagesTarget.hidden = true
    this.composerTarget.hidden = true
    if (this.hasHelpTarget) this.helpTarget.hidden = true
    this.articleTarget.hidden = false
    this.backTarget.classList.remove("hidden")
    this.backTarget.classList.add("flex")
    track("Ask AI Article Opened", { slug: (embedUrl.match(/\/help\/articles\/([^/?#]+)/) || [])[1] || "" })
    try {
      const res = await fetch(embedUrl, { credentials: "same-origin", headers: { Accept: "text/html" } })
      if (!res.ok) throw new Error(String(res.status))
      this.articleBodyTarget.innerHTML = await res.text()
      this.articleBodyTarget.scrollTop = 0
    } catch (err) {
      this.articleBodyTarget.innerHTML = `<p class="text-[13px] text-gray-600">${this._esc(this.t.article_failed || "Couldn't load the article.")}</p>`
    }
  }

  closeArticle() {
    this._articleOpen = false
    this._articleBig(false)
    this._articleChrome(false)
    this.articleTarget.hidden = true
    this.articleBodyTarget.innerHTML = ""
    if (this._helpOpen) { // opened from the Help tab: back to the list it came from
      this.helpTarget.hidden = false
      return
    }
    this.messagesTarget.hidden = false
    this.composerTarget.hidden = !this.homeTarget.classList.contains("hidden")
    this._scroll()
  }

  // Links inside an article: another help article opens here too; anything
  // else opens in a new tab.
  _onArticleBodyClick(e) {
    const a = e.target.closest("a[href]")
    if (!a) return
    const m = (a.getAttribute("href") || "").match(/^(?:https?:\/\/[^/]+)?\/help\/articles\/([^/?#]+)/)
    if (m) {
      e.preventDefault()
      this._openArticle(`/help/articles/${m[1]}/embed`, a.href, a.textContent.trim())
      return
    }
    a.target = "_blank"
    a.rel = "noopener"
  }

  // ── Messages: what they sent the ConsLog team, and the replies (Phelipe
  //    2026-10-16, like Intercom's Messages tab) ───────────────────────

  showHome() {
    this._leaveHelp()
    this._msgsOpen = false
    this._threadOpen = false
    this.msgsTarget.hidden = true
    this.threadTarget.hidden = true
    if (this._articleOpen) this.closeArticle()
    this.messagesTarget.hidden = false
    this._goHome()
    this._paintTabs("home")
  }

  async showMessages() {
    this._leaveHelp()
    if (this._articleOpen) this.closeArticle()
    this._msgsOpen = true
    this._threadOpen = false
    this.messagesTarget.hidden = true
    this.composerTarget.hidden = true
    this.threadTarget.hidden = true
    this.msgsTarget.hidden = false
    this._setHeader("messages")
    this._paintTabs("messages")
    track("Ask AI Messages Opened", { turns: this.history.length })
    this.msgListTarget.innerHTML = `<p class="px-4 py-3 text-[13px] text-gray-500">${this._esc(this.t.messages_loading || "Loading…")}</p>`
    this.msgEmptyTarget.hidden = true
    const [data, convs] = await Promise.all([
      this._getJson(this.messagesUrlValue),
      this.conversationsUrlValue ? this._getJson(this.conversationsUrlValue) : Promise.resolve(null)
    ])
    this._renderMessageRows((data && data.items) || [], (convs && convs.conversations) || [])
  }

  // Only what went through Ask AI (Phelipe 2026-10-16): their Studio changes
  // and the requests it wrote for them — not every Feedback Loop request.
  _renderMessageRows(items, conversations = []) {
    this.msgListTarget.innerHTML = ""
    this.msgEmptyTarget.hidden = items.length > 0 || conversations.length > 0 || this.history.length > 0
    // Their conversations (Phelipe 2026-10-17, like Intercom's): newest first, each
    // with its flow, its last line and how it ended; tapping one brings it back.
    conversations.forEach((c) => {
      const row = document.createElement("button")
      row.type = "button"
      row.dataset.studioConversation = c.id
      row.className = "w-full text-left px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer flex items-start gap-3"
      const title = c.title || this.t.conv_untitled || "New conversation"
      const sub = [c.preview, c.when].filter(Boolean).join(" · ")
      const done = c.status === "done"
      const label = c.outcome_label || (done ? (this.t.conv_done || "Done") : (this.t.conv_open || "Open"))
      row.innerHTML = `
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-2"><span class="text-[14px] font-semibold text-gray-900 truncate">${this._esc(title)}</span>${this._flowPill(c.kind)}</span>
          <span class="block text-[13px] text-gray-500 truncate">${this._esc(sub)}</span>
        </span>
        <span class="shrink-0 mt-0.5 inline-block text-[13px] font-semibold px-2 py-px rounded-full ${done ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-800"}">${this._esc(label)}</span>`
      this.msgListTarget.appendChild(row)
    })
    // The one remembered on this browser only, when the server has none to show (older sessions).
    const lastAsk = conversations.length ? null : [...this.history].reverse().find((m) => m.role === "user")
    if (lastAsk) {
      // Labeled with its flow (Phelipe 2026-10-17): Get help, Studio or Feedback Loop.
      const chat = document.createElement("button")
      chat.type = "button"
      chat.dataset.action = "click->studio-chat#resume"
      chat.className = "w-full text-left px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer flex items-start gap-3"
      chat.innerHTML = `
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-2"><span class="text-[14px] font-semibold text-gray-900 truncate">${this._esc(this.t.resume_chat || "Your conversation")}</span>${this._flowPill(this._door === "studio" ? "studio" : "support")}</span>
          <span class="block text-[13px] text-gray-500 truncate">${this._esc(lastAsk.content)}</span>
        </span>
        <span class="shrink-0 mt-0.5 text-gray-400">›</span>`
      this.msgListTarget.appendChild(chat)
    }
    items.forEach((it) => {
      const change = it.type === "change"
      const status = change ? it.status : this._statusOf(it)
      const n = it.comment_count || 0
      const replies = n ? (n === 1 ? (this.t.replies_one || "1 reply") : (this.t.replies_other || "%{count} replies").replace("%{count}", n)) : ""
      const sub = change ? [this.t.messages_change || "Studio change", it.when] : [it.ref, it.when, replies]
      const row = document.createElement("button")
      row.type = "button"
      if (change) row.dataset.studioChange = it.id; else row.dataset.studioThread = it.id
      row.dataset.title = it.title || ""
      row.dataset.draft = it.draft ? "1" : "0"
      row.className = "w-full text-left px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer flex items-start gap-3"
      row.innerHTML = `
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-2"><span class="text-[14px] text-gray-900 truncate">${this._esc(it.title || "")}</span>${this._flowPill(change ? "studio" : "feedback")}</span>
          <span class="block text-[13px] text-gray-500 truncate">${this._esc(sub.filter(Boolean).join(" · "))}</span>
        </span>
        <span class="shrink-0 mt-0.5 inline-block text-[13px] font-semibold px-2 py-px rounded-full ${change ? this._changeStatusClass(status) : this._statusClass(status)}">${this._esc(change ? this._changeStatusLabel(status) : this._statusLabel(status))}</span>`
      this.msgListTarget.appendChild(row)
    })
  }

  // A small pill naming the flow a Messages row came from: Get help, Studio or Feedback Loop.
  _flowPill(flow) {
    const label = { support: this.t.flow_help || "Get help", studio: this.t.flow_studio || "Studio", feedback: this.t.flow_feedback || "Feedback Loop" }[flow] || flow
    const cls = { support: "bg-brand/10 text-brand-dark", studio: "bg-purple-50 text-purple-700", feedback: "bg-amber-50 text-amber-800" }[flow] || "bg-gray-100 text-gray-600"
    return `<span class="shrink-0 inline-block text-[13px] font-semibold px-1.5 py-px rounded-full ${cls}">${this._esc(label)}</span>`
  }

  // A Studio change from the Messages list: its card, with Undo when allowed.
  async openChange(id, title) {
    this._threadOpen = true
    this._threadId = null
    this.msgsTarget.hidden = true
    this.threadTarget.hidden = false
    this.threadTitleTarget.textContent = title || ""
    this.threadStatusTarget.textContent = ""
    this.threadLinkTarget.href = this.feedbackPageUrlValue
    this.threadBodyTarget.innerHTML = `<p class="text-[13px] text-gray-500">${this._esc(this.t.messages_loading || "Loading…")}</p>`
    const data = await this._getJson(`${this.changeUrlValue}/${id}`)
    if (!data || !data.card_html) {
      this.threadBodyTarget.innerHTML = `<p class="text-[13px] text-gray-600">${this._esc(this.t.network_error)}</p>`
      return
    }
    this.threadStatusTarget.textContent = this._changeStatusLabel(data.status)
    this.threadStatusTarget.className = `shrink-0 inline-block text-[13px] font-semibold px-2 py-px rounded-full ${this._changeStatusClass(data.status)}`
    this.threadBodyTarget.innerHTML = data.card_html
    track("Ask AI Message Opened", { status: data.status, kind: "change" })
  }

  _changeStatusLabel(s) { return (this.t.change_statuses && this.t.change_statuses[s]) || s }

  _changeStatusClass(s) {
    return { live: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-900", off: "bg-gray-200 text-gray-700",
             undone: "bg-gray-100 text-gray-600", rejected: "bg-rose-100 text-rose-800" }[s] || "bg-gray-100 text-gray-700"
  }

  // A request from Messages opens as the Feedback Loop page's own details panel
  // (feedback-detail), inside this window (Phelipe 2026-10-17); a draft goes to
  // the Feedback Loop form, where the recorder is — inside this window too.
  openThread(id, title, draft, from = "messages") {
    if (draft) {
      this._hostFeedback({ resume: id }, "messages")
      return
    }
    this._dtReturn = from
    this._dtHosted = true
    if (this._articleOpen) this.closeArticle()
    this._fbShell(true)
    this._setHeader("detail")
    window.dispatchEvent(new CustomEvent("feedback-detail:open", { detail: { id, title: title || "", host: "studio" } }))
    track("Ask AI Message Opened", { kind: "request" })
  }

  closeThread() {
    this._threadOpen = false
    this._threadId = null
    this.threadTarget.hidden = true
    this.threadBodyTarget.innerHTML = ""
    this.showMessages()
  }

  _statusOf(r) {
    if (r.draft) return "draft"
    if (r.review_state === "pending") return "pending_review"
    return r.status || "new"
  }

  _statusLabel(s) {
    const map = this.t.statuses || {}
    if (map[s]) return map[s]
    if (s === "draft") return this.t.status_draft || "Draft"
    if (s === "pending_review") return this.t.status_pending_review || "Pending review"
    return s
  }

  _statusClass(s) {
    return {
      new: "bg-blue-50 text-blue-700", reopened: "bg-rose-100 text-rose-800", planned: "bg-purple-50 text-purple-700",
      in_progress: "bg-amber-100 text-amber-900", testing: "bg-amber-100 text-amber-900",
      done: "bg-green-100 text-green-800", done_not_in_app: "bg-green-100 text-green-800", exists: "bg-green-100 text-green-800",
      draft: "bg-amber-200 text-amber-900", pending_review: "bg-amber-100 text-amber-900"
    }[s] || "bg-gray-100 text-gray-700"
  }

  _when(iso) {
    if (!iso) return ""
    try { return new Date(iso).toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric" }) } catch (e) { return "" }
  }

  _paintTabs(name) {
    const paint = (el, on) => {
      el.setAttribute("aria-selected", String(on))
      el.classList.toggle("text-gray-900", on)
      el.classList.toggle("font-semibold", on)
      el.classList.toggle("text-gray-500", !on)
    }
    paint(this.tabHomeTarget, name === "home")
    paint(this.tabMessagesTarget, name === "messages")
    if (this.hasTabHelpTarget) paint(this.tabHelpTarget, name === "help")
  }

  // ── Help: the help articles, like Intercom's Help tab (Phelipe 2026-10-16) ──

  showHelp() {
    if (this._articleOpen) { this._helpOpen = false; this.closeArticle() }
    this._msgsOpen = false
    this._threadOpen = false
    this._helpOpen = true
    this.messagesTarget.hidden = true
    this.composerTarget.hidden = true
    this.msgsTarget.hidden = true
    this.threadTarget.hidden = true
    this.helpTarget.hidden = false
    this._setHeader("help")
    this._paintTabs("help")
    track("Ask AI Help Opened", { turns: this.history.length })
    if (!this._helpLoaded) this.helpHome()
  }

  // Leaving the Help tab (Home / Messages): an article opened from it closes too.
  _leaveHelp() {
    if (!this._helpOpen) return
    this._helpOpen = false
    this.helpTarget.hidden = true
    if (this._articleOpen) this.closeArticle()
  }

  async helpHome() {
    this.helpBackTarget.hidden = true
    if (this.helpSearchTarget.value) this.helpSearchTarget.value = ""
    this._helpLoading()
    const data = await this._getJson(this.helpUrlValue)
    if (!data) return this._helpMessage(this.t.help_failed || "Couldn't load the help articles.")
    this._helpLoaded = true
    const cols = data.collections || []
    const head = `<div class="px-4 pt-3 pb-1 text-[15px] font-bold text-gray-900">${this._esc((this.t.help_collections || "%{count} collections").replace("%{count}", cols.length))}</div>`
    this.helpListTarget.innerHTML = head + cols.map((c) => `
      <button type="button" data-action="click->studio-chat#helpSection" data-key="${this._esc(c.key)}" data-title="${this._esc(c.title)}"
              class="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 cursor-pointer">
        <span class="flex-1 min-w-0">
          <span class="block text-[14px] font-semibold text-gray-900">${this._esc(c.title)}</span>
          ${c.description ? `<span class="block text-[13px] text-gray-600 mt-0.5">${this._esc(c.description)}</span>` : ""}
          <span class="block text-[13px] text-gray-500 mt-1">${this._esc(this._helpCount(c.count))}</span>
        </span>
        <span class="text-brand shrink-0">›</span>
      </button>`).join("")
  }

  async helpSection(e) {
    const key = e.currentTarget.dataset.key
    this.helpBackTitleTarget.textContent = e.currentTarget.dataset.title || ""
    this.helpBackTarget.hidden = false
    this._helpLoading()
    const data = await this._getJson(`${this.helpUrlValue}?section=${encodeURIComponent(key)}`)
    if (!data) return this._helpMessage(this.t.help_failed || "Couldn't load the help articles.")
    track("Ask AI Help Collection Opened", { section: key })
    this._renderHelpArticles(data.articles || [], data.description)
  }

  helpSearch() {
    clearTimeout(this._helpTimer)
    const q = this.helpSearchTarget.value.trim()
    if (q.length < 2) { if (!q) this.helpHome(); return }
    this._helpTimer = setTimeout(async () => {
      this.helpBackTitleTarget.textContent = ""
      this.helpBackTarget.hidden = false
      this._helpLoading()
      const data = await this._getJson(`${this.helpUrlValue}?q=${encodeURIComponent(q)}`)
      if (this.helpSearchTarget.value.trim() !== q) return // typed on since
      if (!data) return this._helpMessage(this.t.help_failed || "Couldn't load the help articles.")
      this._renderHelpArticles(data.articles || [])
    }, 300)
  }

  helpArticle(e) {
    e.preventDefault()
    const a = e.currentTarget
    this._openArticle(a.dataset.embed, a.getAttribute("href"), a.dataset.title)
  }

  _renderHelpArticles(articles, description) {
    if (!articles.length) return this._helpMessage(this.t.help_no_results || "No articles match. Try other words, or ask in Home.")
    const intro = description ? `<p class="px-4 pt-3 pb-1 text-[13px] text-gray-600">${this._esc(description)}</p>` : ""
    this.helpListTarget.innerHTML = intro + articles.map((a) => `
      <a href="${this._esc(a.url)}" data-embed="${this._esc(a.embed)}" data-title="${this._esc(a.title)}" data-action="click->studio-chat#helpArticle"
         class="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 text-[14px] text-gray-900">
        <span class="flex-1 min-w-0">${this._esc(a.title)}${a.for ? `<span class="block text-[13px] text-gray-500">${this._esc((this.t.help_for || "For %{who}").replace("%{who}", a.for))}</span>` : ""}</span><span class="text-brand shrink-0">›</span>
      </a>`).join("")
  }

  _helpCount(n) {
    return n === 1 ? (this.t.help_articles_one || "1 article") : (this.t.help_articles_other || "%{count} articles").replace("%{count}", n)
  }

  _helpLoading() { this._helpMessage(this.t.help_loading || "Loading…") }

  _helpMessage(text) {
    this.helpListTarget.innerHTML = `<p class="px-4 py-6 text-center text-[13px] text-gray-500">${this._esc(text)}</p>`
  }

  async _getJson(url) {
    const res = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } }).catch(() => null)
    return res && res.ok ? res.json().catch(() => null) : null
  }

  // ── A person ─────────────────────────────────────────────────────

  person() {
    track("Ask AI Person Requested", { turns: this.history.length })
    this._inChat()
    this._handoff()
    this._persist()
  }

  _handoff() {
    const ctx = this._context()
    const lines = this.history.slice(-6).map((m) => m.role === "assistant" ? `AI: ${m.content.slice(0, 300)}` : `> ${m.content}`)
    const text = `Asked ConsLog AI on ${ctx.page || ctx.path}:\n${lines.join("\n")}\n\n`
    if (this.intercomValue && typeof window.Intercom === "function") {
      window.Intercom("showNewMessage", text)
      this._appendAi(this.t.handoff_done)
      this.close()
    } else {
      const subject = encodeURIComponent(`ConsLog help: ${ctx.page || ""}`.trim())
      this._appendAi(this.t.handoff_mail)
      window.location.href = `mailto:${this.supportEmailValue}?subject=${subject}&body=${encodeURIComponent(text)}`
    }
    this._scroll()
  }

  // ── Clear ────────────────────────────────────────────────────────

  askClear() { this.clearConfirmTarget.classList.remove("hidden"); this.clearConfirmTarget.classList.add("flex") }
  cancelClear() { this.clearConfirmTarget.classList.add("hidden"); this.clearConfirmTarget.classList.remove("flex") }

  clearChat() {
    this.history = []
    this._introDoor = null
    this._conv = null // the server copy stays under Messages; the next line starts a new one
    Array.from(this.messagesTarget.children).forEach((el) => { if (!el.hasAttribute("data-permanent")) el.remove() })
    this.home()
    this.cancelClear()
    try { sessionStorage.removeItem(this.storeKey) } catch (e) { /* private mode */ }
  }

  // ── Bubbles ──────────────────────────────────────────────────────

  _row() {
    const row = document.createElement("div")
    row.className = "space-y-2"
    return row
  }

  _appendUser(text) {
    const row = document.createElement("div")
    row.className = "flex justify-end"
    const bubble = document.createElement("div")
    bubble.className = "max-w-[85%] bg-brand-dark text-white rounded-2xl rounded-br-md px-3 py-2 whitespace-pre-wrap break-words"
    if (this._door === "studio") bubble.style.background = STUDIO_PURPLE
    bubble.textContent = text
    row.appendChild(bubble)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  _appendAi(text, tag = null) {
    if (!text) return null
    const row = document.createElement("div")
    row.className = "flex gap-2 items-start"
    const col = document.createElement("div")
    col.className = "min-w-0 max-w-[92%] bg-white rounded-2xl rounded-tl-md px-3 py-2 shadow-sm ring-1 ring-black/5"
    // Signed (mock v32): a sparkle and the name, purple when it's a Studio change.
    // Studio answers are signed "Studio", Get help answers "Support" in blue, like its card (Phelipe 2026-10-17).
    const support = tag !== "studio" && this._door !== "feedback"
    const who = document.createElement("div")
    who.className = `flex items-center gap-1 text-[13px] font-bold mb-0.5 ${tag === "studio" ? "text-purple-700" : support ? "text-blue-700" : "text-brand-dark"}`
    const name = tag === "studio" ? (this.t.flow_studio || "Studio") : support ? (this.t.subtitle_support || "Support") : (this.t.signed || "ConsLog")
    who.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l1.8 5.6L19.5 9l-5.7 1.5L12 16l-1.8-5.5L4.5 9l5.7-1.4z"/></svg><span>${this._esc(name)}</span>`
    const bubble = document.createElement("div")
    bubble.className = "text-gray-800 whitespace-pre-wrap break-words"
    this._fillAnswer(bubble, text)
    col.appendChild(who)
    col.appendChild(bubble)
    row.appendChild(col)
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  // Numbered steps each on their own line (Phelipe 2026-10-17): the AI often
  // writes "… 1. Open the Bid page 2. Check your company …" in one paragraph.
  // A run counting 1, 2, 3… becomes a list; text before it stays above, and a
  // paragraph after a blank line stays below. Anything else shows as written.
  _fillAnswer(el, text) {
    const marks = []
    const re = /(^|\s)(\d{1,2})[.)]\s+/g
    let m
    while ((m = re.exec(text))) {
      if (+m[2] === marks.length + 1) marks.push({ at: m.index + m[1].length, body: m.index + m[0].length })
    }
    if (marks.length < 2) { el.textContent = text; return }
    const intro = text.slice(0, marks[0].at).trim()
    const steps = marks.map((mk, i) => text.slice(mk.body, i + 1 < marks.length ? marks[i + 1].at : text.length).trim())
    let outro = ""
    const last = steps[steps.length - 1]
    const gap = last.search(/\n\s*\n/)
    if (gap > 0) { outro = last.slice(gap).trim(); steps[steps.length - 1] = last.slice(0, gap).trim() }
    if (intro) { const p = document.createElement("div"); p.textContent = intro; el.appendChild(p) }
    const list = document.createElement("ol")
    list.className = `space-y-1 whitespace-normal${intro ? " mt-1.5" : ""}`
    steps.forEach((s, i) => {
      const li = document.createElement("li")
      li.className = "flex gap-2"
      const n = document.createElement("span")
      n.className = "shrink-0 min-w-[1.25rem] font-semibold text-gray-900"
      n.textContent = `${i + 1}.`
      const body = document.createElement("span")
      body.className = "min-w-0"
      body.textContent = s
      li.appendChild(n)
      li.appendChild(body)
      list.appendChild(li)
    })
    el.appendChild(list)
    if (outro) { const p = document.createElement("div"); p.className = "mt-1.5"; p.textContent = outro; el.appendChild(p) }
  }

  _appendHtml(html) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    const el = tpl.content.firstElementChild
    if (!el) return
    const wrap = document.createElement("div")
    wrap.className = "pl-8"
    wrap.appendChild(el)
    this.messagesTarget.appendChild(wrap)
    this._scroll()
  }

  _appendButtons(buttons) {
    const list = buttons.filter((b) => b && b.label)
    if (!list.length) return
    const wrap = document.createElement("div")
    wrap.className = "pl-8 flex flex-wrap gap-2"
    list.forEach((b) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.studioAct = b.act
      if (b.id) btn.dataset.id = b.id
      if (b.kind) btn.dataset.kind = b.kind
      btn.className = "px-3 h-9 rounded-lg border border-gray-300 bg-white text-gray-700 text-[14px] font-medium hover:bg-gray-50 cursor-pointer"
      btn.textContent = b.label
      wrap.appendChild(btn)
    })
    this.messagesTarget.appendChild(wrap)
  }

  _appendLink(href, label) {
    if (!href || !label) return
    const wrap = document.createElement("div")
    wrap.className = "pl-8"
    const link = document.createElement("a")
    link.href = href
    link.className = "text-[13px] text-brand-dark underline"
    link.textContent = label
    wrap.appendChild(link)
    this.messagesTarget.appendChild(wrap)
  }

  // ── After the hosted Feedback Loop form sends (Phelipe 2026-10-17) ──
  // The form announces "instant-feedback:sent" {request, draft, host}. From a
  // card in this conversation (hosted by the chat, coming back to it): the
  // card's buttons give way to a stamp so it can't go twice, and the chat says
  // what happened with a link to the Feedback Loop. The Home door's own form
  // and a draft resumed from Messages keep their own Sent screen.
  _fbSent({ request, draft, host } = {}) {
    if (!request || host !== "studio" || this._fbReturn !== "chat") return
    const card = this._fbCard
    if (!draft) this._fbCard = null
    if (card?.isConnected) this._stampCard(card, draft ? this.t.fb_draft_stamp : this._sentStamp(request))
    this._appendAi(draft ? this.t.request_draft_kept : this.t.request_sent)
    this._appendLink(this.feedbackPageUrlValue.replace(/\?tab=studio$/, ""), this.t.see_feedback)
    this._convLine("assistant", draft ? this.t.request_draft_kept : this.t.request_sent)
    if (!draft) this._convClose({ outcome_type: "CustomerRequest", outcome_id: request.id, outcome_label: this._sentStamp(request) })
    this._scroll()
    this._persist()
  }

  _sentStamp(request) {
    return String(this.t.fb_sent_stamp || "Sent ✓ #%{id}").replace("%{id}", request?.id ?? "")
  }

  _stampCard(card, text) {
    if (!card) return
    card.querySelector(".px-3.pb-3")?.remove()
    card.querySelector("[data-fb-stamp]")?.remove()
    const stamp = document.createElement("div")
    stamp.className = "px-3 pb-3 text-[13px] font-medium text-green-700"
    stamp.setAttribute("data-fb-stamp", "")
    stamp.textContent = text || ""
    card.appendChild(stamp)
  }

  _appendThinking() {
    const row = document.createElement("div")
    row.className = "flex gap-2 items-center pl-8"
    row.setAttribute("aria-label", this.t.thinking || "Thinking")
    row.innerHTML = '<span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></span><span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style="animation-delay:.15s"></span><span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style="animation-delay:.3s"></span>'
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  _setPending(on) {
    this.pending = on
    this.sendButtonTarget.disabled = on
  }

  _scroll(top = false) {
    requestAnimationFrame(() => { this.messagesTarget.scrollTop = top ? 0 : this.messagesTarget.scrollHeight })
  }

  // ── Plumbing ─────────────────────────────────────────────────────

  // Where the person is, read at send time: the element outlives page changes.
  _context() {
    let meta = {}
    try { meta = JSON.parse(document.querySelector('meta[name="feedback-context"]')?.content || "{}") } catch (e) { meta = {} }
    // The report type picked on a Daily Report form (Phelipe 2026-10-17: "Studio
    // should see the form I'm on"): the server turns it into the form template.
    const typeSel = document.getElementById("daily_report_daily_report_type_id")
    // The exact page (Phelipe 2026-10-17: "don't change the wrong page"): the
    // breadcrumb's segments and the active sub-tab, read from the page itself.
    const bc = document.querySelector('nav.breadcrumb-oneline, nav[aria-label*="readcrumb" i]')
    // Visible crumbs only: the project switcher's closed menu lives in the same
    // nav ("Recent", "↑↓ navigate", "esc close") and must not become part of the page's name.
    // Visible and not the switcher's menu (2026-10-17: "Recent · No projects found · ↑↓ navigate" leaked into a card's page line).
    const shown = (n) => n.getClientRects().length > 0 && !n.closest('[aria-hidden="true"], [role="listbox"], [role="menu"], [role="dialog"], .hidden, .invisible, .opacity-0, .pointer-events-none') && getComputedStyle(n).visibility !== "hidden" && getComputedStyle(n).opacity !== "0"
    // A count badge inside a crumb ("Reports 19", step 2 fix 2026-10-19) is not part of the page's name: badges go, bare numbers go.
    const crumbText = (n) => { const c = n.cloneNode(true); c.querySelectorAll('.rounded-full, .badge, [data-count], sup').forEach((b) => b.remove()); return c.textContent.replace(/\s+/g, " ").trim() }
    const crumbs = bc ? [...bc.querySelectorAll("a, span, li")].filter(shown).map(crumbText).filter((t) => t && t.length < 80 && !/^\d+$/.test(t) && !/↑↓|↵|⌘|esc close|No projects found|^Recent$/i.test(t)) : []
    const breadcrumb = crumbs.filter((t, i) => t !== crumbs[i - 1] && !crumbs.slice(0, i).some((p) => p.includes(t) && p !== t)).join(" › ")
    const tab = [...document.querySelectorAll('[role="tab"][aria-selected="true"]')].filter((t) => !t.closest("#studio-bubble")).map((t) => t.textContent.replace(/\s+/g, " ").trim()).find(Boolean) || ""
    return {
      page: meta.page || document.title.split(" | ")[0],
      path: location.pathname,
      project_id: meta.project_id || "",
      project: meta.project || "",
      report_type_id: typeSel ? typeSel.value : "",
      report_type: typeSel ? (typeSel.selectedOptions?.[0]?.textContent || "").trim() : "",
      breadcrumb: breadcrumb.slice(0, 200),
      tab: tab.slice(0, 60),
      snapshot: this._pageSnapshot(),
      // The spot picked with the dot rides with its "Put it here" line only.
      spot: this._spot || undefined,
      // The block whose pencil was pressed (step 2) rides with the next line only.
      block: this._block || undefined
    }
  }

  // What is on the page right now (Phelipe 2026-10-17: "Studio should be able
  // to see the page — the form, the type selected, everything"): the headings,
  // the form's fields with the choice picked in each dropdown, the tables'
  // headers, the tabs and the main buttons. Words from the screen only —
  // never what was typed into a box, never a figure; the server drops it on
  // finance pages.
  _pageSnapshot() {
    try {
      const txt = (n) => (n?.textContent || "").replace(/\s+/g, " ").trim()
      // Only what the person can see: other report types' forms sit hidden on the same page.
      const ours = (n) => n.closest("#studio-bubble, #instant-feedback, #feedback-detail, nav, header, footer, [data-studio-snapshot=\"skip\"]") || !n.getClientRects().length
      const main = document.querySelector("main") || document.body
      const out = []
      const heads = [...main.querySelectorAll("h1, h2")].filter((h) => !ours(h)).map(txt).filter((t) => t && t.length < 80).slice(0, 6)
      if (heads.length) out.push(`Headings: ${heads.join(" | ")}`)
      const seen = new Set()
      const fields = []
      for (const el of main.querySelectorAll("input, select, textarea")) {
        if (ours(el) || ["hidden", "submit", "button", "search", "file"].includes(el.type)) continue
        const label = (el.labels && el.labels[0] ? txt(el.labels[0]) : "") || el.getAttribute("aria-label") || el.placeholder || ""
        if (!label || label.length > 60 || seen.has(label)) continue
        seen.add(label)
        const kind = el.tagName === "SELECT" ? "dropdown" : el.tagName === "TEXTAREA" ? "long text" : (el.type || "text")
        const picked = el.tagName === "SELECT" ? txt(el.selectedOptions?.[0]).slice(0, 40) : ""
        fields.push(picked ? `${label} (${kind}: ${picked})` : `${label} (${kind})`)
        if (fields.length >= 25) break
      }
      if (fields.length) out.push(`Form fields: ${fields.join("; ")}`)
      const tables = [...main.querySelectorAll("table")].filter((t) => !ours(t)).slice(0, 3)
        .map((t) => [...t.querySelectorAll("thead th")].map((th) => txt(th) || th.getAttribute("title") || "").filter(Boolean).slice(0, 12).join(", ")).filter(Boolean)
      if (tables.length) out.push(`Table columns: ${tables.join(" / ")}`)
      const tabs = [...main.querySelectorAll('[role="tab"]')].filter((t) => !ours(t)).map(txt).filter(Boolean).slice(0, 10)
      if (tabs.length) out.push(`Tabs: ${tabs.join(", ")}`)
      const buttons = [...main.querySelectorAll('button[type="submit"], a.bg-brand, button.bg-brand')].filter((b) => !ours(b)).map(txt).filter((t) => t && t.length < 40).slice(0, 6)
      if (buttons.length) out.push(`Buttons: ${[...new Set(buttons)].join(", ")}`)
      return out.join("\n").slice(0, 1500)
    } catch (e) {
      return ""
    }
  }

  async _post(url, body) {
    const res = await fetch(url, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify(body)
    }).catch(() => null)
    if (!res) return null
    if (res.headers.get("X-Role-Mask-Blocked")) { showToast(this.t.mask_blocked, "error"); return null }
    const data = await res.json().catch(() => null)
    if (!data) return null
    if (!res.ok && !data.reply && !data.error) return null
    return data
  }

  _persist() {
    try { sessionStorage.setItem(this.storeKey, JSON.stringify({ history: this.history, door: this._door, conv: this._conv })) } catch (e) { /* private mode */ }
  }

  // After a full reload: the words come back as plain bubbles (cards don't).
  _restore() {
    let saved = null
    try { saved = JSON.parse(sessionStorage.getItem(this.storeKey) || "null") } catch (e) { saved = null }
    if (!saved || !Array.isArray(saved.history) || !saved.history.length) return
    this.history = saved.history.slice(-MAX_HISTORY)
    this._door = saved.door || null
    this._conv = saved.conv && saved.conv.id ? saved.conv : null
    this._introDoor = this._door
    // The bubble opens on Home; the chat waits under Messages (Phelipe 2026-10-17).
    this.history.forEach((m) => m.role === "user" ? this._appendUser(m.content) : this._appendAi(m.content))
    this._homeOnly(true)
  }

  _esc(s) {
    return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
